import { createPopper } from '@popperjs/core';
import classnames from 'classnames';
import React, { createRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const Popover = ({
  children,
  className,
  innerRef,
  modifiers,
  onFirstUpdate,
  placement,
  size,
  strategy,
  triggerSelector,
}) => {
  const [isVisible, setIsVisible] = useState();
  const sizeString = size ? `popover-${size}` : null;
  const triggerRef = useRef();

  // Toggle "visibility" css property as "isVisible" changes

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    innerRef.current.style.visibility = isVisible ? 'visible' : '';
  }, [innerRef, isVisible]);

  // Set the "triggerRef" when "triggerSelector" changes

  useEffect(() => {
    triggerRef.current = document.querySelector(triggerSelector);
  }, [triggerSelector]);

  // Create event listeners to toggle "isVisible" based on whether a click is within the target or outside of it

  useEffect(() => {
    triggerRef.current.addEventListener('click', () => {
      setIsVisible((prevIsVisible) => !prevIsVisible);
    });

    document.addEventListener('click', (e) => {
      if (!triggerRef.current.contains(e.target) && !innerRef.current.contains(e.target)) {
        setIsVisible(false);
      }
    });
  }, [innerRef]);

  // Create popover

  useEffect(() => {
    const popper = createPopper(triggerRef.current, innerRef.current, {
      modifiers,
      placement,
      strategy,
      onFirstUpdate,
    });

    return () => popper.destroy();
  }, [innerRef, modifiers, onFirstUpdate, placement, strategy]);

  return (
    <div className={classnames('popover', className, sizeString)} ref={innerRef} data-testid="Popover">
      {children}
    </div>
  );
};

Popover.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.func]),
  modifiers: PropTypes.arrayOf(PropTypes.shape()),
  onFirstUpdate: PropTypes.func,
  placement: PropTypes.oneOf([
    'auto',
    'auto-start',
    'auto-end',
    'top',
    'top-start',
    'top-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'right',
    'right-start',
    'right-end',
    'left',
    'left-start',
    'left-end',
  ]),
  size: PropTypes.oneOf(['xs', 'sm', 'lg', 'xl']),
  strategy: PropTypes.oneOf(['absolute', 'fixed']),
  triggerSelector: PropTypes.string.isRequired,
};

Popover.defaultProps = {
  children: null,
  className: null,
  innerRef: createRef(),
  modifiers: [],
  onFirstUpdate: null,
  placement: 'bottom',
  size: null,
  strategy: 'absolute',
};

export default Popover;
